<template>
  <div class="customer-support-details" :style="appStyleProps">
    <div class="customer-support-text">Customer support</div>
    <a class="customer-support-anchor small"
       :class="{'customized-affiliate': !!affiliateCustomizations}"
       :href="`tel:+${phone}`">
      {{ phone }}
    </a>
    <client-only>
      <div v-if="hours" class="customer-support-text small">{{ hours }}</div>
    </client-only>
    <a class="customer-support-anchor small"
       :class="{'customized-affiliate': !!affiliateCustomizations}"
       :href="`mailto:${email}`">
      {{ email }}
    </a>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import env from '~/libs/env'
import { parse } from 'date-fns'
import { toZonedTime, format } from 'date-fns-tz'
import { phone as formatPhone } from '~/libs/format'
import { storeToRefs } from 'pinia'
import { useAffiliateCustomizationStore } from '~/store/affiliate-customization'

const affiliateCustomizationStore = useAffiliateCustomizationStore()
const {
  appStyleProps,
  affiliateCustomizations,
  contactInfoPhone,
  contactInfoEmail,
  contactInfoHours,
  isEmbeddedLayout,
} = storeToRefs(affiliateCustomizationStore)

const timeZone = ref('')
const mdtVsMst = ref('')

onMounted(() => {
  timeZone.value = Intl.DateTimeFormat().resolvedOptions().timeZone ?? 'America/Denver'
  const date = new Date()
  const zonedDate = toZonedTime(date, 'America/Denver')
  mdtVsMst.value = format(zonedDate, 'x', { timeZone: 'America/Denver' })
})

const phone = computed(() => {
  const phoneNumber = isEmbeddedLayout.value ? (contactInfoPhone.value || env('embeddedSupportPhone')) : '855-853-6346'

  return formatPhone(phoneNumber)
})

const email = computed(() => {
  return isEmbeddedLayout.value ? (contactInfoEmail.value || env('embeddedSupportEmail')) : env('customerSupportEmail')
})

const hours = computed(() => {
  if (typeof contactInfoHours.value === 'string') {
    return contactInfoHours.value
  }
  // lendio default hours
  const today = format(new Date(), 'yyyy-MM-dd')
  const start = format(
    parse(
      `${today} 8:00 AM ${mdtVsMst.value}`, 'yyyy-MM-dd h:mm a x', new Date()
    ), 'h:mm a z', { timeZone: timeZone.value }
  )
  const end = format(
    parse(
      `${today} 5:00 PM ${mdtVsMst.value}`, 'yyyy-MM-dd h:mm a x', new Date()
    ),
    'h:mm a zzz',
    { timeZone: timeZone.value }
  )
  return `M-F ${start} - ${end}`.replace(/:00 /g, '')
})
</script>

<style scoped lang="scss">
@import 'assets/styles/affiliate-customizations/affiliate-customization-mixins';

.customer-support-details {
  padding: 24px 28px;

  .customer-support-text {
    font-size: 14px;
    font-weight: 600;
    line-height: 20px;
    letter-spacing: 0.1px;
    color: $app-nav-text-primary;
  }

  .customer-support-anchor {
    color: $background-100;
    text-decoration-color: $background-100;

    &.customized-affiliate {
      @include affiliateAppAnchorTag();
    }
  }

  .customer-support-text,
  .customer-support-anchor {
    &.small {
      font-size: 12px;
      font-weight: 400;
      line-height: 16px;
    }
  }
}
</style>
